import { useCallback, useEffect, useState } from 'react';

import { flattenTree, type ItemChangedReason, type TreeItems } from '@/components/ui/sortable-tree';
import { type ReorderOperation, treeItemsToPages } from './page-utils';
import {
  getReorderVisibilityWarning,
  type PendingReorderVisibilityWarning,
} from './page-visibility-warning-utils';
import type { SortablePageItemData } from './sortable-page-item';

interface UsePageReorderWarningParams {
  treeItems: TreeItems<SortablePageItemData>;
  onPagesReorder?: (
    operation: ReorderOperation,
    nextTree: ReturnType<typeof treeItemsToPages>,
  ) => void | Promise<void>;
}

export function usePageReorderWarning({ treeItems, onPagesReorder }: UsePageReorderWarningParams) {
  const [localTreeItems, setLocalTreeItems] = useState<TreeItems<SortablePageItemData>>(treeItems);
  const [pendingReorderWarning, setPendingReorderWarning] =
    useState<PendingReorderVisibilityWarning | null>(null);

  useEffect(() => {
    setLocalTreeItems(treeItems);
  }, [treeItems]);

  const handleItemsChanged = useCallback(
    (
      newItems: TreeItems<SortablePageItemData>,
      reason: ItemChangedReason<SortablePageItemData>,
    ) => {
      if (reason.type !== 'dropped' || !onPagesReorder) {
        setLocalTreeItems(newItems);
        return;
      }

      const { draggedItem, droppedToParent } = reason;
      const flatItems = flattenTree(newItems);

      const draggedPageId = String(draggedItem.id);
      const newParentId = droppedToParent ? String(droppedToParent.id) : null;
      const siblings = flatItems.filter(
        (item) => String(item.parentId ?? null) === String(newParentId),
      );
      const targetIndex = siblings.findIndex((item) => String(item.id) === draggedPageId);
      const previousSiblingId = targetIndex > 0 ? String(siblings[targetIndex - 1].id) : null;
      const operation = {
        pageId: draggedPageId,
        parentId: newParentId,
        previousSiblingId,
      };
      const warning = getReorderVisibilityWarning({
        previousItems: localTreeItems,
        newItems,
        draggedPage: draggedItem.page,
        draggedPageId,
      });

      if (warning) {
        setPendingReorderWarning({
          newItems,
          operation,
        });
        return;
      }

      setLocalTreeItems(newItems);
      void onPagesReorder(operation, treeItemsToPages(newItems));
    },
    [localTreeItems, onPagesReorder],
  );

  const confirmPendingReorder = useCallback(async () => {
    if (!pendingReorderWarning || !onPagesReorder) return;

    await onPagesReorder(
      pendingReorderWarning.operation,
      treeItemsToPages(pendingReorderWarning.newItems),
    );
    setLocalTreeItems(pendingReorderWarning.newItems);
    setPendingReorderWarning(null);
  }, [onPagesReorder, pendingReorderWarning]);

  return {
    confirmPendingReorder,
    handleItemsChanged,
    localTreeItems,
    pendingReorderWarning,
    setPendingReorderWarning,
  };
}
